import { Usage } from '@activepieces/shared';

const MS_PER_DAY = 86_400_000;
const MIN_DAYS_FOR_PREDICTION = 3;

/**
 * Predicts the date when the monthly task quota will be exhausted, based on
 * the average consumption since the start of the current month.
 *
 * Assumes period starts on the 1st of the month and consumption is linear.
 * Returns null when no reliable prediction can be made.
 */
type QuotaExhaustionPrediction = {
  date: Date;
  daysRemaining: number;
};

export const predictQuotaExhaustion = (
  usage: Pick<Usage, 'tasks' | 'resetDate'>,
  quota: number,
  now: Date = new Date(),
): QuotaExhaustionPrediction | null => {
  if (quota <= 0 || usage.tasks <= 0 || usage.tasks >= quota) {
    return null;
  }

  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const daysElapsed = (now.getTime() - periodStart.getTime()) / MS_PER_DAY;

  if (daysElapsed < MIN_DAYS_FOR_PREDICTION) {
    return null;
  }

  const dailyRate = usage.tasks / daysElapsed;
  const remaining = quota - usage.tasks;
  const daysUntilExhaustion = remaining / dailyRate;
  const date = new Date(now.getTime() + daysUntilExhaustion * MS_PER_DAY);

  const resetDate = new Date(usage.resetDate);
  if (date >= resetDate) {
    return null;
  }

  return {
    date,
    daysRemaining: Math.max(1, Math.round(daysUntilExhaustion)),
  };
};
